import { AssetId, InstalledCamera, SiteId, TenantId, CameraId as CId } from "@visioni/a3s-schema";
import { AssetCameraKeySchema, SiteIdCodec } from "@visioni/a3s-schema/codecs";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { Authorizer } from ".";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { AuthorizationManagementService } from "./client-services";
import z from "zod/v4";
import { Json } from "@visioni/a3s-codecs";

const Wildcard = z.literal("*");

const SiteManagerRoleString = z.templateLiteral(["site_manager:", z.string()]);
const SiteManagerRole = z.codec(SiteManagerRoleString, z.custom<`site_manager:${string}`>(), {
  decode: (val) => {
    const id = val.substring("site_manager:".length);
    return `site_manager:${Wildcard.or(SiteIdCodec).parse(id)}` as const;
  },
  encode: (val) => val,
});

const SiteUserRoleString = z.templateLiteral(["site_user:", z.string()]);
const SiteUserRole = z.codec(SiteUserRoleString, z.custom<`site_user:${string}`>(), {
  decode: (val) => {
    const id = val.substring("site_user:".length);
    return `site_user:${Wildcard.or(SiteIdCodec).parse(id)}` as const;
  },
  encode: (val) => val,
});
export const EditableRole = z.union([SiteManagerRole, SiteUserRole]);
export type EditableRole = z.infer<typeof EditableRole>;

export const TenantAdminRole = z.literal("tenant_admin");
export type TenantAdminRole = z.infer<typeof TenantAdminRole>;

export const Role = z.union([SiteManagerRole, SiteUserRole, TenantAdminRole]);
export type Role = z.infer<typeof Role>;

export const CameraId = z.union([
  z.literal("*"),
  z.templateLiteral([z.string(), z.literal("/*")]).transform((val) => {
    const siteId = val.substring(0, val.length - 2);
    return `${SiteIdCodec.parse(siteId)}/*` as const;
  }),
  z.templateLiteral([z.string(), z.literal("/"), z.string()]).transform((val) => {
    const siteId = val.substring(0, val.indexOf("/"));
    const assetCameraKey = val.substring(val.indexOf("/") + 1);

    return `${SiteIdCodec.parse(siteId)}/${AssetCameraKeySchema.parse(assetCameraKey)}` as const;
  }),
]);
export type CameraId = z.infer<typeof CameraId>;

export const TargetObject = z.union([
  z.templateLiteral(["camera:", z.string()]).transform((val) => {
    const cameraId = val.substring("camera:".length);
    return `camera:${CameraId.parse(cameraId)}` as const;
  }),
]);
export type TargetObject = z.infer<typeof Object>;

export const cameraActions = [
  "livestream:view",
  "alarm-schedules:manage",
  "camera:view",
  "nickname:set",
  "clips:view",
  "deterrence:trigger",
] as const;
export const CameraAction = z.enum(cameraActions);
export type CameraAction = z.infer<typeof CameraAction>;

export const cameraRoles = ["camera_viewer", "camera_manager"] as const;
export const CameraRole = z.enum(cameraRoles);
export type CameraRole = z.infer<typeof CameraRole>;

export const PermissionObject = z
  .tuple([
    z.union([Role, z.templateLiteral(["user:", z.string()])]),
    z.string().transform((tenant) => TenantId.fromString(tenant.substring("tenant:".length))),
    TargetObject,
    CameraAction,
  ])
  .transform(([userOrRole, tenant, object, action]) => ({
    userOrRole,
    tenant,
    object,
    action,
  }));
export type PermissionObject = z.infer<typeof PermissionObject>;

/**
 * This codec should be used in combination with {@link AuthorizationManagementService.getRawPoliciesForUser} and {@link Authorizer.fromSerializedPermissions} to serialize and deserialize raw policies.
 */
export const SerializedPermissionsCodec = Json(z.string().array().array());

export const FULL_ACCESS_PERMISSIONS: CameraAction[] = [
  "livestream:view",
  "alarm-schedules:manage",
  "camera:view",
  "nickname:set",
  "clips:view",
  "deterrence:trigger",
];

export const READ_ONLY_PERMISSIONS: CameraAction[] = ["livestream:view", "camera:view", "clips:view"];

export function accessCameraIdFromInstalledCamera(camera: InstalledCamera): CameraId {
  return CameraId.parse(
    `${SiteId.toString(camera.site)}/${AssetId.toString(camera.asset)}@${CId.toString(camera.camera)}`,
  );
}
